import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, FileIcon, LabeledData, ModalV2, Tag, TextInput } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import {
  resolveAttributeKey,
  semanticOf,
  sortGlobalsByRolePriority,
  STATUS_ALIASES,
} from "#common/attributeKeys";
import { DocumentPreview } from "#common/DocumentPreview";
import { isClearableDataType } from "#common/graphql/Document";
import { MiniAttrGrid, type MiniAttrCellEditor, type MiniAttrRow } from "#common/MiniAttrGrid";
import { DOCUMENT_LIFECYCLE_LABELS } from "#common/types/vault";
import { optionIdsFor, splitMultiValueAgainstOptions } from "#helpers/multi-select-value";
import { useUploadFilePicker } from "../UploadDocumentModal/useFilePicker";
import { makeReferenceCellEditors } from "./ReferenceCellEditor";
import * as styles from "./styles_vanilla.css";
import type {
  AttrDataType,
  GlobalAttribute,
  ReferenceData,
  Space,
  SpaceLocalAttribute,
  VaultDocument,
} from "#common/types/vault";
import type { AttributeEdit, DocumentDetailProps } from "./types";
import type { UploadFileDescriptor } from "../UploadDocumentModal/types";

export type { AttributeEdit, DocumentDetailProps };

type Editor =
  | { kind: "select"; options: string[] }
  | { kind: "multi-select"; options: string[] }
  | { kind: "text" | "date" | "number" };

/**
 * The reference attributes that participate in the client cascade. A row's role is
 * its MEANING, not its key: environments name these differently, and the cascade
 * has to fire regardless of what this catalog calls them.
 */
type ReferenceRole = "client" | "entity" | "investor";

type EditRow = {
  key: string;
  scope: "global" | "local";
  label: string;
  value: string;
  sp?: string;
  editor: Editor;
  /** The attribute's catalog type — decides whether a clear can be persisted. */
  dataType: AttrDataType;
  /** Set for the reference rows the cascade knows about. */
  role?: ReferenceRole;
  /** System-derived (Client ID, Document Status) — read-only with a lock. */
  derived?: boolean;
  /** Required attribute — an empty editable cell surfaces a fill-in CTA. */
  flag?: boolean;
  /**
   * Backend option id for each of this row's select options, keyed by display
   * value. Set only for Single-select / Multi-select rows — resolves a picked
   * value back to the id `valueIds` requires (DV-466/DV-544), since the
   * generic select editor reports only the display value, not an id.
   */
  optionIds?: Record<string, string>;
};

/**
 * Which attributes are system-derived — locked, with a lock glyph — because no
 * edit here can change them: Document Status follows the sharing state, and
 * Client ID is the access-control code derived from the client.
 *
 * The Client REFERENCE is NOT derived: it is picked at upload and editable here.
 * The two are easy to conflate, because a catalog attribute *labelled* "Client"
 * can be *keyed* `client_id` — which `semanticOf` reads as the Client ID
 * attribute. So the label is the tiebreaker, and anything ambiguous stays
 * editable: locking on a key guess is the worse failure, and it is exactly what
 * left "Client" read-only with no way to set a value.
 */
const isDerivedAttribute = (a: GlobalAttribute): boolean => {
  const semantic = semanticOf(a.key);
  if (semantic === "status") return true;
  return semantic === "clientId" && looksLikeIdLabel(a.name);
};

/**
 * Does this display label name an ID rather than the thing it identifies?
 *
 * Separators are stripped before matching, then `id` has to be the SUFFIX. A word
 * boundary (`/\bid\b/`) looked equivalent but silently missed the concatenated
 * spelling — there is no boundary between the `t` and the `I` of "ClientID" — which
 * would have left the real access-control attribute EDITABLE, the exact drift the
 * lock exists to prevent. `CLIENT_ID_ALIASES` already anticipates the camelCase key
 * (`clientID`), so the label showing up that way is not hypothetical.
 *
 * Suffix-matching is safe because the only caller has already narrowed to a
 * clientId-KEYED attribute: the label there is "Client", "Client ID" or "ClientID",
 * not an unrelated word that happens to end in those two letters.
 */
const looksLikeIdLabel = (label: string): boolean =>
  /id$/i.test(label.replace(/[^a-z0-9]/gi, ""));

/**
 * Which cascade role an attribute plays, if any. Client → Entity → LP/Investor is
 * a hierarchy: an entity belongs to a client, so changing or clearing the client
 * invalidates both of the others.
 *
 * The client role covers a catalog that keys the client reference `client_id` while
 * labelling it "Client" — the same conflation {@link isDerivedAttribute} untangles,
 * so the two agree by construction: a clientId-keyed attribute is either the
 * derived *code* or the client *reference*, never both.
 */
const referenceRoleOf = (a: GlobalAttribute): ReferenceRole | undefined => {
  const semantic = semanticOf(a.key);
  if (semantic === "entity") return "entity";
  if (semantic === "investor") return "investor";
  if (semantic === "client" || (semantic === "clientId" && !isDerivedAttribute(a)))
    return "client";
  return undefined;
};

/**
 * Dependents to clear per role. `client` invalidates entity and investor;
 * `entity` invalidates investor. Only a genuine id change cascades — the
 * placeholder→resolved double-report must not wipe a just-picked dependent.
 */
const CASCADE_BY_ROLE: Partial<Record<ReferenceRole, ReferenceRole[]>> = {
  client: ["entity", "investor"],
  entity: ["investor"],
};

function editorForGlobal(a: GlobalAttribute, reference?: ReferenceData): Editor {
  const semantic = semanticOf(a.key);
  if (semantic === "status") return { kind: "select", options: ["Internal", "External"] };
  if (a.dataType === "Yes/No") return { kind: "select", options: ["Yes", "No"] };
  if (a.dataType === "Single-select") return { kind: "select", options: a.values ?? [] };
  if (a.dataType === "Multi-select") return { kind: "multi-select", options: a.values ?? [] };
  if (a.dataType === "Reference" && reference) {
    // Keyed by MEANING: a literal-key map returned nothing wherever the
    // environment named the attribute differently (`entity_id` for Entity),
    // leaving the editor as a free-text box instead of a picker.
    const bySemantic: Partial<Record<string, string[]>> = {
      client: reference.clients,
      entity: Object.values(reference.entities).flat(),
      investor: reference.investors,
      period: reference.periods,
    };
    const byKey: Record<string, string[] | undefined> = {
      fundAdmin: reference.fundAdmins,
      assetClass: reference.assetClasses,
    };
    const o = (semantic && bySemantic[semantic]) ?? byKey[a.key];
    if (o) return { kind: "select", options: o };
  }
  if (a.dataType === "Date") return { kind: "date" };
  if (a.dataType === "Number") return { kind: "number" };
  return { kind: "text" };
}

function editorForLocal(l: SpaceLocalAttribute): Editor {
  if (l.dataType === "Yes/No") return { kind: "select", options: ["Yes", "No"] };
  if (l.dataType === "Single-select") return { kind: "select", options: l.options ?? [] };
  if (l.dataType === "Multi-select") return { kind: "multi-select", options: l.options ?? [] };
  if (l.dataType === "Date") return { kind: "date" };
  if (l.dataType === "Number") return { kind: "number" };
  return { kind: "text" };
}

/** How a Multi-select's several picks are joined into one display string. */
const OPTION_SEPARATOR = ", ";

/**
 * The display value for a Single-/Multi-select row, resolving saved option IDS
 * back to their labels.
 *
 * A select attribute persists its picks as ids (`valueIds`) and the document
 * query fetches no name list for them, so what arrives here is a raw id —
 * `"opt-a"`, or `"opt-a, opt-b"` for a Multi-select. An id is never one of the
 * editor's options, and AG Grid's select editor falls back to the FIRST option
 * whenever the cell value isn't in its list, so opening the cell overwrote the
 * saved pick and reported an edit nobody made (DV-607).
 *
 * A part the catalog doesn't know is passed through untouched: it is either
 * already a label, or a stale id — better shown than blanked.
 */
const selectDisplayValue = (value: string, optionIds?: Record<string, string>): string => {
  if (!value || !optionIds) return value;
  const labelById = new Map(Object.entries(optionIds).map(([label, id]) => [id, label]));
  return value
    .split(OPTION_SEPARATOR)
    .map((part) => labelById.get(part) ?? part)
    .join(OPTION_SEPARATOR);
};

/** The catalog id map for a row, set only where a picked value has an id to resolve. */
const catalogOptionIds = (
  dataType: AttrDataType,
  optionIdsByValue?: Record<string, string>,
): Record<string, string> | undefined =>
  dataType === "Single-select" || dataType === "Multi-select" ? optionIdsByValue : undefined;

function globalRowsFor(
  doc: VaultDocument,
  space: Space | null,
  globalLib: GlobalAttribute[],
  reference?: ReferenceData,
): EditRow[] {
  if (!space) return [];
  const statusKey = resolveAttributeKey(STATUS_ALIASES, globalLib);
  return sortGlobalsByRolePriority(
    globalLib
      .filter((a) => space.globals[a.key]?.on)
      .map((a) => {
        const derived = isDerivedAttribute(a);
        const optionIds = catalogOptionIds(a.dataType, a.optionIdsByValue);
        return {
          key: a.key,
          scope: "global" as const,
          label: a.name,
          value: selectDisplayValue(
            a.key === statusKey ? doc.status : (doc.attrs[a.key] ?? ""),
            optionIds,
          ),
          editor: editorForGlobal(a, reference),
          dataType: a.dataType,
          role: derived ? undefined : referenceRoleOf(a),
          derived,
          flag: !derived && !!space.globals[a.key]?.required,
          optionIds,
        };
      }),
    globalLib,
  );
}

function localRowsFor(doc: VaultDocument, space: Space | null): EditRow[] {
  if (space) {
    return space.locals.map((l) => {
      const optionIds = catalogOptionIds(l.dataType, l.optionIdsByValue);
      return {
        key: l.name,
        scope: "local" as const,
        label: l.name,
        sp: space.name,
        value: selectDisplayValue(doc.local[l.name] ?? "", optionIds),
        editor: editorForLocal(l),
        dataType: l.dataType,
        flag: !!l.required,
        optionIds,
      };
    });
  }
  return Object.entries(doc.local)
    .filter(([, v]) => v)
    .map(([k, v]) => ({
      key: k,
      scope: "local",
      label: k,
      value: v,
      sp: "",
      editor: { kind: "text" },
      // No space contract to read a type from; these rows are display-only.
      dataType: "Text",
    }));
}

/**
 * Map detail rows to the shared MiniAttrGrid shape, showing any staged edit in
 * place of the saved value. Derived rows are always read-only, regardless of
 * whether editing is wired up.
 */
function toMiniRows(
  rows: EditRow[],
  editable: boolean,
  draft: DraftEdits,
  /** Reference rows swap their option list for a live search editor when supplied. */
  referenceEditors?: Partial<Record<ReferenceRole, MiniAttrCellEditor>>,
): MiniAttrRow[] {
  return rows.map((r) => {
    const staged = draft[formKeyOf(r)];
    const searchEditor = r.role ? referenceEditors?.[r.role] : undefined;
    return {
      key: r.key,
      scope: r.scope,
      attribute: r.label,
      space: r.sp,
      value: staged ? staged.value : r.value,
      editable: editable && !r.derived,
      editor: searchEditor ? { kind: "component", component: searchEditor } : r.editor,
      derived: r.derived,
      flag: r.flag,
      optionIds: r.optionIds,
    };
  });
}

/** The editable attribute rows for the form — space's active globals (minus the
    system-derived ones) plus every space-local, each carrying its editor. */
function editableRowsFor(
  doc: VaultDocument,
  space: Space | null,
  globalLib: GlobalAttribute[],
  reference?: ReferenceData,
): EditRow[] {
  const globals = globalRowsFor(doc, space, globalLib, reference).filter((r) => !r.derived);
  const locals = space ? localRowsFor(doc, space) : [];
  return [...globals, ...locals];
}

const formKeyOf = (row: { scope: "global" | "local"; key: string }): string =>
  `${row.scope}:${row.key}`;

/**
 * One staged edit: the display value, plus the picked ids when it came from a
 * reference search. `ids: undefined` on a staged row means "no id for this value" —
 * distinct from an unstaged row, which keeps the ids the document was saved with.
 */
type DraftEdit = { value: string; ids?: string[] };

/**
 * Inline edits staged but not yet saved, keyed by `${scope}:${key}`. A key is
 * present only once its cell has actually been edited, which is what separates
 * "the user set this" from "this is what the document already says" — the save
 * payload needs that distinction to know whose picked ids are still valid.
 */
type DraftEdits = Record<string, DraftEdit>;

export function DocumentDetail({
  document,
  space,
  globalLib,
  access,
  reference,
  previewUrl,
  previewLoading = false,
  previewError,
  onDownload,
  onDownloadVersion,
  onClose,
  onNewVersion,
  savingVersion = false,
  onEditAttr,
  onSaveAttrs,
  savingAttrs = false,
  referenceSearch = false,
}: DocumentDetailProps) {
  const [tab, setTab] = useState<"details" | "history">("details");
  const [previewOpen, setPreviewOpen] = useState(true);
  const [composing, setComposing] = useState(false);
  const [pendingFile, setPendingFile] = useState<UploadFileDescriptor | null>(null);
  const [versionError, setVersionError] = useState<string | null>(null);
  // The compose modal stages exactly one file, so `onPickVersionFile` only
  // ever reads `picked[0]` — but `multiple: false` below is what actually
  // keeps the OS dialog from letting a user select more than one in the
  // first place, rather than silently dropping the rest of a multi-select.
  const onPickVersionFile = useCallback((picked: UploadFileDescriptor[]) => {
    if (picked[0]) setPendingFile(picked[0]);
  }, []);
  const { open: openVersionFilePicker, input: versionFilePickerInput } = useUploadFilePicker(
    onPickVersionFile,
    undefined,
    false,
  );
  const [note, setNote] = useState("");
  const [draft, setDraft] = useState<DraftEdits>({});
  const dirty = Object.keys(draft).length > 0;
  /**
   * Ids reported by an open reference editor, keyed like the draft. A REF, not
   * state: staging mid-edit would replace the grid's `rowData` and AG Grid closes
   * the editor whose rows changed. Read back when the cell's value commits.
   */
  const pickedIdsRef = useRef<Record<string, string[] | undefined>>({});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // With staged edits, Escape discards them rather than closing the overlay,
      // so an accidental Escape can't throw away work by unmounting.
      if (e.key !== "Escape") return;
      if (dirty) setDraft({});
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, dirty]);

  const globalRows = useMemo(
    () => (document ? globalRowsFor(document, space, globalLib, reference) : []),
    [document, space, globalLib, reference],
  );
  const localRows = useMemo(
    () => (document ? localRowsFor(document, space) : []),
    [document, space],
  );
  const editRows = useMemo(
    () => (document ? editableRowsFor(document, space, globalLib, reference) : []),
    [document, space, globalLib, reference],
  );

  /** The row playing a given cascade role, if the space references one. */
  const roleRow = (role: ReferenceRole) => globalRows.find((r) => r.role === role);
  const clientRow = roleRow("client");
  const entityRow = roleRow("entity");
  /**
   * The client currently in effect — what scopes the entity and investor searches.
   *
   * A staged pick wins OUTRIGHT, including a staged *clear*: falling back to the
   * document's saved id when the staged entry has none would leave the entity
   * search scoped to the client the user just removed.
   */
  const stagedClient = clientRow ? draft[formKeyOf(clientRow)] : undefined;
  const effectiveClientId = stagedClient
    ? stagedClient.ids?.[0]
    : clientRow
      ? document?.attrIds?.[clientRow.key]?.[0]
      : undefined;

  /**
   * The entity currently in effect — what scopes the investor position search.
   * Same staged-wins logic as the client above.
   */
  const stagedEntity = entityRow ? draft[formKeyOf(entityRow)] : undefined;
  const effectiveEntityId = stagedEntity
    ? stagedEntity.ids?.[0]
    : entityRow
      ? document?.attrIds?.[entityRow.key]?.[0]
      : undefined;

  /**
   * Reference editors, mounted only where their queries can run. Memoised on the
   * scoping client and entity: a new component identity mid-edit would remount
   * the open editor, and remounting on every keystroke would make the typeahead
   * unusable.
   */
  const batched = !!onSaveAttrs;
  const referenceEditors = useMemo(() => {
    if (!referenceSearch || !batched) return undefined;
    const { ClientCellEditor, EntityCellEditor, InvestorCellEditor } = makeReferenceCellEditors(
      {
        space,
        clientId: effectiveClientId,
        entityId: effectiveEntityId,
        reportIds: (row, ids) => {
          pickedIdsRef.current[formKeyOf(row)] = ids;
        },
      },
    );
    return {
      client: ClientCellEditor,
      entity: EntityCellEditor,
      investor: InvestorCellEditor,
    } as Partial<Record<ReferenceRole, MiniAttrCellEditor>>;
  }, [referenceSearch, batched, space, effectiveClientId, effectiveEntityId]);

  if (!document) return null;

  /**
   * A quarantined document's fill-in gap, recomputed on every render so it tracks
   * staged edits live: a required row counts as missing until its EFFECTIVE value
   * (the staged draft, falling back to the saved value) is non-blank.
   */
  const missingCount = editRows.filter((row) => {
    const effective = draft[formKeyOf(row)]?.value ?? row.value;
    return !!row.flag && effective.trim() === "";
  }).length;
  const isQuarantined = document.lifecycle === "quarantined";

  const ext = (document.file || "doc").toLowerCase();
  const externalGrants = access.filter((g) => g.kind !== "internal");
  const isShared = externalGrants.length > 0;

  const resetCompose = () => {
    setComposing(false);
    setPendingFile(null);
    setNote("");
    setVersionError(null);
  };
  const saveVersion = () => {
    const file = pendingFile?.source;
    if (!file) return;
    setVersionError(null);
    const result = onNewVersion?.(document.id, {
      note: note.trim(),
      file,
      fileName: pendingFile.name,
    });
    if (result && typeof result.then === "function") {
      void result.then(resetCompose, (e: unknown) => {
        setVersionError(e instanceof Error ? e.message : "Upload failed");
      });
    } else {
      resetCompose();
    }
  };

  /**
   * Editing is inline in both grids. `onSaveAttrs` stages each edit and commits
   * the batch from the footer; `onEditAttr` is the uncommitted alternative, where
   * every cell edit fires on its own. Either one makes the cells editable.
   */
  const canEditAttrs = batched || !!onEditAttr;

  /**
   * Without a space, the space-local rows are a read-only echo of whatever values
   * the document happens to carry — there is no contract saying which attributes
   * exist, so they are not part of the save payload either. Offering an editor
   * there would stage a change that Save then silently dropped.
   */
  const canEditLocals = canEditAttrs && !!space;

  /**
   * Stage (batched) or immediately emit (uncommitted) one cell's new value.
   *
   * Picking a different client clears Entity and LP/Investor; picking a different
   * entity clears LP/Investor. Only a GENUINE change cascades: a reference pick
   * reports twice (id-as-placeholder, then the resolved name), and treating the
   * second as a change would wipe a dependent the user had just filled in.
   */
  const editCell = (row: MiniAttrRow, value: string) => {
    const fk = formKeyOf(row);
    // A reference search reports its own picked id via the ref; a plain select
    // (Single/Multi-select) has none, so resolve the chosen display value(s)
    // back to backend option ids here — the only place both are available.
    // Multi-select cells commit comma-joined labels; splitting is a no-op for
    // a single pick. An unresolved / cleared value has no id.
    const fromCatalog = optionIdsFor(
      splitMultiValueAgainstOptions(value, Object.keys(row.optionIds ?? {})),
      row.optionIds,
      { keepUnmappedIds: true },
    );
    const ids = pickedIdsRef.current[fk] ?? (fromCatalog?.length ? fromCatalog : undefined);
    delete pickedIdsRef.current[fk];

    if (!batched) {
      onEditAttr?.(document.id, row.scope, row.key, value);
      return;
    }

    const isClient = clientRow?.key === row.key && row.scope === "global";
    const isEntity = entityRow?.key === row.key && row.scope === "global";
    const clientChanged = isClient && (ids?.[0] ?? undefined) !== effectiveClientId;
    const entityChanged = isEntity && (ids?.[0] ?? undefined) !== effectiveEntityId;

    setDraft((d) => {
      const next: DraftEdits = { ...d, [fk]: { value, ids } };
      const triggeredRole = clientChanged ? "client" : entityChanged ? "entity" : undefined;
      if (!triggeredRole) return next;
      (CASCADE_BY_ROLE[triggeredRole] ?? []).forEach((role) => {
        const dependent = roleRow(role);
        // Already empty and never staged: nothing to clear, and staging it would
        // report a dirty drawer for an edit the user didn't make.
        if (!dependent) return;
        const current = d[formKeyOf(dependent)]?.value ?? dependent.value;
        if (!current) return;
        next[formKeyOf(dependent)] = { value: "", ids: undefined };
      });
      return next;
    });
  };

  /**
   * Staged clears the API cannot express, so Save must not pretend they worked.
   *
   * `updateVaultDocument` upserts, and an id-shaped attribute (reference / select)
   * has no empty representation — `valueIds` must be non-empty. Required attributes
   * (global or space-local) cannot be cleared even when the type is text — the
   * backend rejects empty `valueText` for guaranteed bindings.
   */
  const blockedClears = editRows.filter((row) => {
    const staged = draft[formKeyOf(row)];
    if (!staged || staged.value.trim() !== "") return false;
    if (row.flag) return true;
    return !isClearableDataType(row.dataType);
  });

  /**
   * A Single-/Multi-select row whose staged value has no backend option id.
   * `row.optionIds` is only ever set for these two data types (see {@link
   * EditRow.optionIds}), so its presence is what marks the row id-shaped here —
   * a picked value with nothing in that map failed to resolve. Saving it would
   * stage `valueText` for a type the backend only accepts `valueIds` for, so
   * this blocks Save the same way {@link blockedClears} does for an
   * unsendable clear, instead of letting the request fail after the fact.
   */
  const blockedUnresolvedIds = editRows.filter((row) => {
    const staged = draft[formKeyOf(row)];
    return !!staged && staged.value !== "" && !!row.optionIds && !staged.ids?.length;
  });

  const saveAttrs = () => {
    if (blockedClears.length > 0 || blockedUnresolvedIds.length > 0) return;
    /**
     * ONLY the rows the user actually edited. `updateVaultDocument` upserts, so an
     * omitted attribute keeps its current value — there is nothing to preserve by
     * resending it, and resending costs something: the subgraph persists an
     * id-shaped value by REPLACING its rows, so an untouched reference would be
     * deleted and reinserted, churning rows (and any audit built on them) for
     * attributes nobody touched.
     *
     * It also removes the reason untouched rows carried their saved ids at all —
     * with nothing untouched in the payload, there is no id-backed reference for a
     * save to downgrade to plain text.
     */
    const edits: AttributeEdit[] = editRows.flatMap((row) => {
      const staged = draft[formKeyOf(row)];
      if (!staged) return [];
      return [
        {
          scope: row.scope,
          key: row.key,
          value: staged.value,
          // Whatever the reference search picked; undefined when typed or cleared.
          ids: staged.ids,
          // A deliberately emptied scalar must reach the wire as an empty column;
          // without this the builder drops it and the upsert keeps the old value.
          clear: staged.value === "",
        },
      ];
    });
    const result = onSaveAttrs?.(document.id, edits);
    // Clear the staged edits once the save settles (immediately if synchronous):
    // the refetched document becomes the displayed value.
    if (result && typeof result.then === "function") {
      void result.then(
        () => setDraft({}),
        () => undefined,
      );
    } else {
      setDraft({});
    }
  };

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} aria-hidden />

      {previewOpen && (
        <div className={styles.preview}>
          <div className={styles.previewHead}>
            <div className={styles.dphLeft}>
              <FileIcon extension={ext} size="medium" />
              <div className={styles.dphName} title={document.name}>
                {document.name}.{ext}
              </div>
            </div>
            <div className={styles.dphActs}>
              <button
                className={styles.closeBtn}
                aria-label="Download document"
                type="button"
                // Presentational use has nothing to download, so the action is
                // disabled rather than a button that silently does nothing.
                disabled={!onDownload}
                onClick={onDownload}
              >
                <Icon type="download" size="small" />
              </button>
              <button
                className={styles.closeBtn}
                onClick={() => setPreviewOpen(false)}
                aria-label="Collapse preview"
                type="button"
              >
                <Icon type="arrow-right-to-line" size="small" />
              </button>
            </div>
          </div>
          <div className={styles.previewBody}>
            <DocumentPreview
              extension={ext}
              name={`${document.name}.${ext}`}
              url={previewUrl}
              loading={previewLoading}
              error={previewError}
              hint="Use the download button above."
            />
          </div>
        </div>
      )}

      <aside
        className={previewOpen ? styles.drawer.split : styles.drawer.full}
        role="dialog"
        aria-label="Document detail"
      >
        {!previewOpen && (
          <button
            className={styles.reopenTab}
            onClick={() => setPreviewOpen(true)}
            type="button"
          >
            <Icon type="file-alt" size="small" />
            <span>Preview</span>
          </button>
        )}
        <div className={styles.head}>
          <div className={styles.headMain}>
            <h2 className={styles.title}>{document.name}</h2>
            {isQuarantined && (
              <div className={styles.headWarning}>
                <Icon
                  type="exclamation-triangle"
                  size="small"
                  customColor={styles.HEAD_WARNING_COLOR}
                />
                <span>
                  {missingCount > 0
                    ? `Missing ${missingCount} attribute${missingCount === 1 ? "" : "s"}`
                    : DOCUMENT_LIFECYCLE_LABELS.quarantined}
                </span>
              </div>
            )}
          </div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <Icon type="xmark" size="medium" />
          </button>
        </div>
        <div className={styles.tabs} role="tablist">
          <button
            role="tab"
            aria-selected={tab === "details"}
            className={tab === "details" ? styles.tab.on : styles.tab.off}
            onClick={() => setTab("details")}
            type="button"
          >
            Document details
          </button>
          <button
            role="tab"
            aria-selected={tab === "history"}
            className={tab === "history" ? styles.tab.on : styles.tab.off}
            onClick={() => setTab("history")}
            type="button"
          >
            Version history
          </button>
        </div>

        <div className={styles.body}>
          {tab === "details" ? (
            <>
              <div className={styles.panel}>
                <div className={styles.panelHead}>
                  <h3 className={styles.panelHeadTitle}>Sharing</h3>
                </div>
                <div className={styles.panelBody}>
                  <div className={styles.kvList}>
                    <LabeledData
                      variant="muted"
                      label="Sharing status"
                      value={isShared ? "Shared externally" : "Internal only"}
                    />
                    {isShared ? (
                      externalGrants.map((g, i) => (
                        <LabeledData key={i} variant="muted" label={g.name} value={g.detail} />
                      ))
                    ) : (
                      <LabeledData
                        variant="muted"
                        label="To share externally"
                        value="Match a live rule, or add to a collection"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.panel}>
                <div className={styles.panelHead}>
                  <h3 className={styles.panelHeadTitle}>Global attributes</h3>
                  {canEditAttrs && globalRows.some((r) => !r.derived) && (
                    <span className={styles.panelHeadSub}>Click a value to edit</span>
                  )}
                </div>
                <div className={styles.panelBody}>
                  {globalRows.length > 0 ? (
                    <MiniAttrGrid
                      rows={toMiniRows(globalRows, canEditAttrs, draft, referenceEditors)}
                      onEdit={editCell}
                    />
                  ) : (
                    <div className={styles.attrEmpty}>No global attributes.</div>
                  )}
                </div>
              </div>

              <div className={styles.panel}>
                <div className={styles.panelHead}>
                  <h3 className={styles.panelHeadTitle}>Space-local attributes</h3>
                  {canEditLocals && localRows.length > 0 && (
                    <span className={styles.panelHeadSub}>Click a value to edit</span>
                  )}
                </div>
                <div className={styles.panelBody}>
                  {localRows.length > 0 ? (
                    <MiniAttrGrid
                      showSpace
                      rows={toMiniRows(localRows, canEditLocals, draft)}
                      onEdit={editCell}
                    />
                  ) : (
                    <div className={styles.attrEmpty}>
                      No space-local attributes defined by {space ? space.name : "this space"}.
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <h3 className={styles.panelHeadTitle}>Version history</h3>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span className={styles.panelHeadSub}>
                    {document.versions.length}{" "}
                    {document.versions.length === 1 ? "version" : "versions"}
                  </span>
                  <Button
                    variant="neutral"
                    icon="upload"
                    iconPosition="left"
                    onClick={() => setComposing(true)}
                  >
                    Upload new version
                  </Button>
                </div>
              </div>
              <div className={styles.panelBody}>
                <div className={styles.vtimeline}>
                  {document.versions.map((v, i) => {
                    const latest = i === 0;
                    const last = i === document.versions.length - 1;
                    return (
                      <div className={styles.vtItem} key={`${v.n}-${i}`}>
                        {!last && <span className={styles.vtRail} aria-hidden />}
                        <span className={styles.vtNode}>
                          <Icon type="circle-check" size="large" />
                        </span>
                        <div className={styles.vtBody}>
                          <div className={styles.vtTitle}>
                            <span>
                              <span className={styles.vtStrong}>{v.n}</span> uploaded by{" "}
                              <span className={styles.vtStrong}>{v.who}</span>
                              {v.role && ` (${v.role})`}
                            </span>
                            {latest && <Tag variant="success" label="Current" />}
                          </div>
                          <div className={styles.vtSub}>
                            <span>{v.date}</span>
                            {v.note && <span className={styles.vtNote}>{v.note}</span>}
                          </div>
                        </div>
                        <button
                          className={styles.vtDownload}
                          aria-label={`Download ${v.n}`}
                          type="button"
                          disabled={!onDownloadVersion}
                          onClick={() => onDownloadVersion?.(v.n)}
                        >
                          <Icon type="download" size="small" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/*
         * Quarantined documents can't be filed until every required attribute is
         * filled in, so this bar stays up regardless of `dirty` for them — closing
         * it only on a staged edit would hide the one action that resolves the gap.
         * Non-quarantined documents keep the dirty-only "Save changes" bar.
         */}
        {tab === "details" && (isQuarantined || dirty) && (
          <div className={styles.saveBar}>
            {blockedClears.length > 0 ? (
              <span className={styles.saveBarBlocked} role="alert">
                Pick a value for {blockedClears.map((r) => r.label).join(", ")} — clearing
                {blockedClears.length === 1 ? " it" : " them"} can&rsquo;t be saved yet
              </span>
            ) : blockedUnresolvedIds.length > 0 ? (
              <span className={styles.saveBarBlocked} role="alert">
                Pick a matching option for {blockedUnresolvedIds.map((r) => r.label).join(", ")}{" "}
                — the typed value
                {blockedUnresolvedIds.length === 1 ? " isn't" : "s aren't"} a valid choice
              </span>
            ) : isQuarantined && missingCount > 0 ? (
              <span className={styles.saveBarBlocked} role="alert">
                Fill in {missingCount} more required attribute{missingCount === 1 ? "" : "s"} to
                file this document
              </span>
            ) : isQuarantined ? (
              <span className={styles.saveBarNote}>Ready to file</span>
            ) : (
              <span className={styles.saveBarNote}>Unsaved changes</span>
            )}
            <div className={styles.saveBarActions}>
              <Button
                variant="neutral"
                disabled={savingAttrs}
                onClick={isQuarantined ? onClose : () => setDraft({})}
              >
                {isQuarantined ? "Cancel" : "Discard"}
              </Button>
              <Button
                variant="primary"
                // A blocked clear or an unresolved select can't be saved, so Save
                // says so by being unavailable rather than silently dropping it; a
                // quarantined document additionally needs every gap filled first.
                disabled={
                  savingAttrs ||
                  blockedClears.length > 0 ||
                  blockedUnresolvedIds.length > 0 ||
                  (isQuarantined && missingCount > 0)
                }
                onClick={saveAttrs}
              >
                {isQuarantined ? "Resolve & file" : "Save changes"}
              </Button>
            </div>
          </div>
        )}
      </aside>

      {composing && (
        <ModalV2
          show={composing}
          onHide={resetCompose}
          size="small"
          closeButton
          headerIconProps={{ type: "upload" }}
          title="Upload new version"
          description={`This will become v${document.versions.length + 1} of “${document.name}”`}
        >
          <ModalV2.Body>
            {versionFilePickerInput}
            {!pendingFile ? (
              <button className={styles.vcDrop} onClick={openVersionFilePicker} type="button">
                <Icon type="upload" size="large" />
                <span className={styles.vcDropTitle}>
                  Choose a file to upload as v{document.versions.length + 1}
                </span>
                <span className={styles.vcDropSub}>
                  Replaces the current version; the old one stays in history
                </span>
              </button>
            ) : (
              <div className={styles.vcFile}>
                <FileIcon extension={ext} size="medium" />
                <div className={styles.vcFileName} title={pendingFile.name}>
                  {pendingFile.name}
                </div>
                <button
                  className={styles.closeBtn}
                  onClick={() => setPendingFile(null)}
                  aria-label="Remove file"
                  type="button"
                  disabled={savingVersion}
                >
                  <Icon type="xmark" size="small" />
                </button>
              </div>
            )}
            <div className={styles.vcNote}>
              <TextInput
                label="What changed?"
                placeholder="e.g. Updated call amount"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={savingVersion}
              />
            </div>
            {versionError && (
              <span className={styles.saveBarBlocked} role="alert">
                {versionError}
              </span>
            )}
          </ModalV2.Body>
          <ModalV2.Footer>
            <div className={styles.modalFooter}>
              <Button variant="neutral" onClick={resetCompose} disabled={savingVersion}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!pendingFile || savingVersion}
                onClick={saveVersion}
              >
                {savingVersion ? "Uploading…" : "Save version"}
              </Button>
            </div>
          </ModalV2.Footer>
        </ModalV2>
      )}
    </>
  );
}
